import { Button, Typography } from '@breetometer/components';
import { yupResolver } from '@hookform/resolvers/yup';
import { Otp } from 'BreetComponents';
import { getApiError, handleApiError, PINLENGTH, showToast } from 'BreetHelpers';
import { useCreatePinMutation, useResetPinMutation, useUpdatePinMutation, useUpdatePreferenceMutation } from 'BreetRedux';
import { type FormEvent, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import {
	CreatePinSchema,
	type CreatePinSchemaType,
	formPinPageProps,
	type IFormPin,
	UpdatePinSchema,
	type UpdatePinSchemaType,
} from './FormPin.helpers';

const FormPin = ({ onSuccess, mode, otp }: IFormPin) => {
	const [createPin, { isLoading: createPinLoading }] = useCreatePinMutation();
	const [updatePin, { isLoading: updatePinLoading }] = useUpdatePinMutation();
	const [resetPin, { isLoading: resetPinLoading }] = useResetPinMutation();
	const [updatePreference] = useUpdatePreferenceMutation();

	const createResetHookForm = useForm({
		resolver: yupResolver(CreatePinSchema),
		defaultValues: { confirmPin: '', pin: '' },
	});

	const updateHookForm = useForm({
		resolver: yupResolver(UpdatePinSchema),
		defaultValues: { oldPin: '', pin: '' },
	});

	const onChangeCreateResetInput = useCallback(
		(key: 'pin' | 'confirmPin', pin: string) => {
			createResetHookForm.setValue(key, pin, { shouldValidate: true, shouldDirty: true });
		},
		[createResetHookForm]
	);

	const onChangeUpdateInput = useCallback(
		(key: 'pin' | 'oldPin', pin: string) => {
			updateHookForm.setValue(key, pin, { shouldValidate: true, shouldDirty: true });
		},
		[updateHookForm]
	);

	const handlePinApi = useCallback(
		({ pin, oldPin }: { pin: string; oldPin: string }) => {
			if (mode === 'create') return createPin({ pin });
			if (mode === 'update') return updatePin({ pin, oldPin });
			return resetPin({ pin, otp: otp ?? '' });
		},
		[createPin, mode, otp, resetPin, updatePin]
	);

	const formProps = formPinPageProps[mode];

	const onHandlePinMutationSuccess = useCallback(() => {
		if (mode === 'create') {
			updatePreference({ activatePin: true })
				.unwrap()
				.then(() => {
					showToast('success', formProps.successMessage);
				})
				.catch(handleApiError);
		} else {
			showToast('success', formProps.successMessage);
		}
		onSuccess?.();
	}, [formProps.successMessage, mode, onSuccess, updatePreference]);

	const handlePinMutation = useCallback(
		(data: CreatePinSchemaType | UpdatePinSchemaType) => {
			handlePinApi({ pin: data.pin, oldPin: 'oldPin' in data ? data.oldPin : '' })
				.unwrap()
				.then(onHandlePinMutationSuccess)
				.catch((error: unknown) => {
					const apiError = getApiError(error);
					const alreadySetPinMsg = 'you already have pin code set, you can only update/recover your pin code';
					if (mode === 'create' && apiError === alreadySetPinMsg) {
						onHandlePinMutationSuccess();
					} else {
						showToast('error', apiError);
					}
				});
		},
		[handlePinApi, mode, onHandlePinMutationSuccess]
	);

	const handleFormSubmit = useCallback(
		async (e: FormEvent<HTMLFormElement>) => {
			if (mode === 'update') {
				await updateHookForm.handleSubmit(handlePinMutation)(e);
			} else {
				await createResetHookForm.handleSubmit(handlePinMutation)(e);
			}
		},
		[createResetHookForm, handlePinMutation, mode, updateHookForm]
	);

	const handleOTPInputChange = useCallback(
		(section: 'first' | 'second') => (pin: string) => {
			const updateMap = {
				first: 'oldPin',
				second: 'pin',
			} as const;

			const createResetMap = {
				first: 'pin',
				second: 'confirmPin',
			} as const;

			if (mode === 'update') {
				onChangeUpdateInput(updateMap[section], pin);
			} else {
				onChangeCreateResetInput(createResetMap[section], pin);
			}
		},
		[mode, onChangeCreateResetInput, onChangeUpdateInput]
	);

	const getOtpError = useCallback(
		(section: 'first' | 'second') => {
			const updateErrors = {
				first: updateHookForm.formState.errors.oldPin?.message,
				second: updateHookForm.formState.errors.pin?.message,
			} as const;

			const createResetErrors = {
				first: createResetHookForm.formState.errors.pin?.message,
				second: createResetHookForm.formState.errors.confirmPin?.message,
			} as const;

			return mode === 'update' ? updateErrors[section] : createResetErrors[section];
		},
		[
			mode,
			updateHookForm.formState.errors.oldPin?.message,
			updateHookForm.formState.errors.pin?.message,
			createResetHookForm.formState.errors.pin?.message,
			createResetHookForm.formState.errors.confirmPin?.message,
		]
	);

	const disableBtn = mode === 'update' ? !updateHookForm.formState.isValid : !createResetHookForm.formState.isValid;

	return (
		<div className='pinverification'>
			<div className='pinverification_header'>
				<Typography
					as='h1'
					className='color-neutral-50'
				>
					{formProps.title}
				</Typography>

				{!!formProps.description && <Typography className='color-primary-50'>{formProps.description}</Typography>}
			</div>

			<form onSubmit={handleFormSubmit}>
				<Typography
					as='label'
					className='color-primary-50'
				>
					{formProps.label1}
				</Typography>

				<Otp.Input
					otpLength={PINLENGTH}
					position='center'
					onChange={handleOTPInputChange('first')}
					error={getOtpError('first')}
					id='pin'
					showError
					data-testid='pin-input'
				/>

				<Typography
					as='label'
					className='color-primary-50'
				>
					{formProps.label2}
				</Typography>

				<Otp.Input
					otpLength={PINLENGTH}
					position='center'
					onChange={handleOTPInputChange('second')}
					error={getOtpError('second')}
					id='confirmPin'
					showError
					data-testid='confirm-pin-input'
				/>

				<Button
					label={formProps.buttonTitle}
					disabled={disableBtn}
					type='submit'
					isLoading={createPinLoading || updatePinLoading || resetPinLoading}
				/>
			</form>
		</div>
	);
};

export default FormPin;
